import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import QuoteCard from "@/components/QuoteCard";

const CATS = ["All", "Politics", "Business", "Travel", "Automotive", "Fashion", "Tech", "Food", "Books", "Sports", "Opinion"];
const SCORES = [
  { label: "Any", val: 0 },
  { label: "Candid (70+)", val: 70 },
  { label: "Raw (85+)", val: 85 },
  { label: "Brutal (92+)", val: 92 },
];
const SORTS = [
  { label: "Recent", val: "recent" },
  { label: "Trending", val: "trending" },
  { label: "Most Honest", val: "honest" },
];
const STATUS_FILTERS = [
  { label: "All", val: "all" },
  { label: "✓ Verified only", val: "verified" },
  { label: "Editorial paraphrases", val: "paraphrase" },
  { label: "🤖 AI-illustrated", val: "ai_illustrated" },
];

export default function Feed() {
  const [params, setParams] = useSearchParams();
  const [quotes, setQuotes] = useState([]);
  const [cat, setCat] = useState(params.get("cat") || "All");
  const [score, setScore] = useState(0);
  const [sort, setSort] = useState("recent");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    const p = { limit: 500, sort };
    if (cat !== "All") p.category = cat;
    if (score) p.min_score = score;
    api.get("/quotes", { params: p }).then(r => setQuotes(r.data));
    // reflect selected category in URL
    if (cat === "All") { if (params.get("cat")) setParams({}); }
    else { setParams({ cat }); }
  }, [cat, score, sort]);

  const filtered = useMemo(() => {
    let list = quotes;
    if (status !== "all") {
      list = list.filter(x => {
        const st = x.verification_status || (x.verified ? "verified" : "paraphrase");
        return st === status;
      });
    }
    if (q.trim()) {
      const t = q.toLowerCase();
      list = list.filter(x =>
        x.text.toLowerCase().includes(t) ||
        (x.speaker || "").toLowerCase().includes(t) ||
        (x.tags || []).join(" ").toLowerCase().includes(t)
      );
    }
    return list;
  }, [quotes, q, status]);

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 fade-up">
      <div className="border-b-ink pb-8 mb-10">
        <div className="tag-red mb-3 inline-block">The Feed</div>
        <h1 className="font-display text-5xl md:text-7xl leading-[0.95]">Every "to be very honest," on record.</h1>
        <p className="font-body mt-4 max-w-2xl">
          Filter by industry, filter by candour. Each entry links to a source and — where useful — to the products, tickets, or books it triggers.
        </p>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-8">
        <input
          data-testid="feed-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search speaker, keyword, tag…"
          className="border-ink bg-white px-4 py-3 font-body w-full md:w-80"
        />
        <div className="flex gap-2 flex-wrap" data-testid="feed-category-filters">
          {CATS.map(c => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`pill ${cat === c ? "active" : ""}`}
              data-testid={`filter-cat-${c.toLowerCase()}`}
            >{c}</button>
          ))}
        </div>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap items-center" data-testid="feed-status-filters">
        <span className="font-ui text-[10px] opacity-60">Show</span>
        {STATUS_FILTERS.map(s => (
          <button
            key={s.val}
            onClick={() => setStatus(s.val)}
            className={`pill ${status === s.val ? "active" : ""}`}
            data-testid={`filter-status-${s.val}`}
          >{s.label}</button>
        ))}
      </div>

      <div className="flex gap-2 mb-6 flex-wrap items-center" data-testid="feed-sort-filters">
        <span className="font-ui text-[10px] opacity-60">Sort</span>
        {SORTS.map(s => (
          <button
            key={s.val}
            onClick={() => setSort(s.val)}
            className={`pill ${sort === s.val ? "active" : ""}`}
            data-testid={`sort-${s.val}`}
          >{s.label}</button>
        ))}
      </div>

      <div className="flex gap-2 mb-10 flex-wrap" data-testid="feed-score-filters">
        {SCORES.map(s => (
          <button
            key={s.val}
            onClick={() => setScore(s.val)}
            className={`pill ${score === s.val ? "active" : ""}`}
            data-testid={`filter-score-${s.val}`}
          >{s.label}</button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6" data-testid="feed-grid">
        {filtered.map(x => <QuoteCard key={x.id} q={x} />)}
        {filtered.length === 0 && (
          <div className="font-quote text-2xl opacity-70 col-span-full" data-testid="feed-empty">
            To be very honest… nothing matches. Loosen the filters.
          </div>
        )}
      </div>

      {filtered.length > 0 && (
        <div className="mt-10 text-center font-mono text-xs opacity-70" data-testid="feed-count">
          Showing {filtered.length} of {quotes.length} filed statements.
        </div>
      )}
    </div>
  );
}
